import React, { useState, useEffect, useMemo } from "react";
import { getData, postData, putData, deleteData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaPuzzlePiece,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle,
  FaTags,
  FaList
} from "react-icons/fa";

function AccessoriesManager() {
  const [activeTab, setActiveTab] = useState('accessories');
  const [accessories, setAccessories] = useState([]);
  const [accessoryTypes, setAccessoryTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInactiveAccessories, setShowInactiveAccessories] = useState(false);
  const [showInactiveTypes, setShowInactiveTypes] = useState(false);
  
  // Estados para accesorios
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingAccessory, setEditingAccessory] = useState(null);
  const [deletingAccessory, setDeletingAccessory] = useState(null);
  const [formData, setFormData] = useState({
    accessory_name: '',
    accessory_type: '',
    active: 1
  });

  // Estados para tipos de accesorios
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showDeleteTypeModal, setShowDeleteTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deletingType, setDeletingType] = useState(null);
  const [typeFormData, setTypeFormData] = useState({
    accessory_type_name: '',
    active: 1
  });

  // Estados para eliminación permanente de accesorios
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [permanentDeletingAccessory, setPermanentDeletingAccessory] = useState(null);

  // Estados para eliminación permanente de tipos
  const [showPermanentDeleteTypeModal, setShowPermanentDeleteTypeModal] = useState(false);
  const [permanentDeletingType, setPermanentDeletingType] = useState(null);

  useEffect(() => {
    loadAccessories();
    loadAccessoryTypes();
  }, []);

  const loadAccessories = async () => {
    try {
      setLoading(true);
      
      const response = await getData('/api/admin/get_accessories');
      
      if (response && response.data && response.data.accessories) {
        setAccessories(response.data.accessories);
      } else {
        setAccessories([]);
      }
      
    } catch (error) {
      console.error('Error cargando accesorios:', error);
      toastError('Error al cargar los accesorios');
      setAccessories([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAccessoryTypes = async () => {
    try {
      const response = await getData('/api/admin/get_accessory_types');
      
      let typesData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          typesData = response.data;
        } else if (response.data.accessoryTypes && Array.isArray(response.data.accessoryTypes)) {
          typesData = response.data.accessoryTypes;
        }
      }
      
      setAccessoryTypes(typesData);
      
    } catch (error) {
      console.error('Error cargando tipos de accesorios:', error);
      toastError('Error al cargar los tipos de accesorios');
      setAccessoryTypes([]);
    }
  };

  const loadAccessoriesOnly = async () => {
    try {
      const response = await getData('/api/admin/get_accessories');
      if (response?.data?.accessories) {
        setAccessories(response.data.accessories);
      }
    } catch (error) {
      console.error('Error recargando accesorios:', error);
    }
  };

  const loadTypesOnly = async () => {
    try {
      const response = await getData('/api/admin/get_accessory_types');
      let typesData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          typesData = response.data;
        } else if (response.data.accessoryTypes) {
          typesData = response.data.accessoryTypes;
        }
      }
      setAccessoryTypes(typesData);
    } catch (error) {
      console.error('Error recargando tipos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          accessory_name: formData.accessory_name,
          accessory_type: parseInt(formData.accessory_type),
          active: formData.active,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };

      if (editingAccessory) {
        dataToSend.data.id = editingAccessory.id;
        const response = await putData('/api/admin/update_accessory', dataToSend);
        
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al actualizar');
        }
        
        setAccessories(prev => prev.map(acc => 
          acc.id === editingAccessory.id
            ? { 
                ...acc, 
                accessory_name: formData.accessory_name,
                accessory_type: parseInt(formData.accessory_type),
                accessory_type_name: accessoryTypes.find(t => t.id === parseInt(formData.accessory_type))?.accessory_type_name || acc.accessory_type_name,
                active: formData.active
              }
            : acc
        ));
        
        toastSuccess(`Accesorio "${formData.accessory_name}" actualizado exitosamente`);
      } else {
        const response = await postData('/api/admin/create_accessory', dataToSend);
        
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al crear');
        }
        
        if (response.data?.accessory) {
          const newAccessory = {
            id: response.data.accessory.id || response.data.id,
            accessory_name: formData.accessory_name,
            accessory_type: parseInt(formData.accessory_type),
            accessory_type_name: accessoryTypes.find(t => t.id === parseInt(formData.accessory_type))?.accessory_type_name || '',
            active: formData.active
          };
          setAccessories(prev => [newAccessory, ...prev]);
        }
        
        toastSuccess(`Accesorio "${formData.accessory_name}" creado exitosamente`);
        loadAccessoriesOnly().catch(() => {});
      }
      
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error guardando accesorio:', error);
      toastError(error.message || (editingAccessory ? 'Error al actualizar el accesorio' : 'Error al crear el accesorio'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingAccessory(null);
    setFormData({
      accessory_name: '',
      accessory_type: '',
      active: 1
    });
  };

  const handleEdit = (accessory) => {
    setEditingAccessory(accessory);
    setFormData({
      accessory_name: accessory.accessory_name || '',
      accessory_type: accessory.accessory_type || '',
      active: accessory.active !== undefined ? accessory.active : 1
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingAccessory(null);
    setFormData({
      accessory_name: '',
      accessory_type: '',
      active: 1
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (accessory) => {
    setDeletingAccessory(accessory);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingAccessory) return;
    
    try {
      setLoading(true);
      
      // Usar PUT para marcar como inactivo en lugar de eliminar físicamente
      const dataToSend = {
        data: {
          id: deletingAccessory.id,
          accessory_name: deletingAccessory.accessory_name,
          accessory_type: deletingAccessory.accessory_type, // Campo requerido por el backend
          active: 0, // Marcar como inactivo
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      const response = await putData('/api/admin/update_accessory', dataToSend);
      
      // Verificar si la respuesta es un error
      if (response.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al desactivar');
      }
      
      // Actualización optimista: marcar como inactivo
      setAccessories(prevAccessories => prevAccessories.map(accessory => 
        accessory.id === deletingAccessory.id ? { ...accessory, active: 0 } : accessory
      ));
      
      toastSuccess(`Accesorio "${deletingAccessory.accessory_name}" desactivado exitosamente`);
      setShowDeleteModal(false);
      setDeletingAccessory(null);
    } catch (error) {
      console.error('Error desactivando accesorio:', error);
      toastError(error.message || 'Error al desactivar el accesorio');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingAccessory(null);
  };

  // Funciones para tipos de accesorios
  const handleTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          accessory_type_name: typeFormData.accessory_type_name,
          active: typeFormData.active,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };

      if (editingType) {
        dataToSend.data.id = editingType.id;
        const response = await putData('/api/admin/update_accessory_type', dataToSend);
        
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al actualizar');
        }
        
        setAccessoryTypes(prev => prev.map(type => 
          type.id === editingType.id
            ? { ...type, accessory_type_name: typeFormData.accessory_type_name, active: typeFormData.active }
            : type
        ));
        
        toastSuccess(`Tipo "${typeFormData.accessory_type_name}" actualizado exitosamente`);
      } else {
        const response = await postData('/api/admin/create_accessory_type', dataToSend);
        
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al crear');
        }
        
        if (response.data?.accessoryType) {
          const newType = {
            id: response.data.accessoryType.id || response.data.id,
            accessory_type_name: typeFormData.accessory_type_name,
            active: typeFormData.active
          };
          setAccessoryTypes(prev => [newType, ...prev]);
        }
        
        toastSuccess(`Tipo "${typeFormData.accessory_type_name}" creado exitosamente`);
        loadTypesOnly().catch(() => {});
      }
      
      setShowTypeModal(false);
      resetTypeForm();
    } catch (error) {
      console.error('Error guardando tipo:', error);
      toastError(editingType ? 'Error al actualizar el tipo' : 'Error al crear el tipo');
    } finally {
      setLoading(false);
    }
  };

  const resetTypeForm = () => {
    setEditingType(null);
    setTypeFormData({
      accessory_type_name: '',
      active: 1
    });
  };

  const handleTypeEdit = (type) => {
    setEditingType(type);
    setTypeFormData({
      accessory_type_name: type.accessory_type_name || '',
      active: type.active !== undefined ? type.active : 1
    });
    setShowTypeModal(true);
  };

  const handleTypeNew = () => {
    setEditingType(null);
    setTypeFormData({
      accessory_type_name: '',
      active: 1
    });
    setShowTypeModal(true);
  };

  const handleTypeInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTypeFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTypeDelete = (type) => {
    setDeletingType(type);
    setShowDeleteTypeModal(true);
  };

  const confirmTypeDelete = async () => {
    if (!deletingType) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          id: deletingType.id,
          accessory_type_name: deletingType.accessory_type_name,
          active: 0, // Marcar como inactivo
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      await putData('/api/admin/update_accessory_type', dataToSend);
      toastSuccess(`Tipo "${deletingType.accessory_type_name}" eliminado exitosamente`);
      setShowDeleteTypeModal(false);
      setDeletingType(null);
      await loadAccessoryTypes();
      await loadAccessories(); // Recargar accesorios para actualizar referencias
    } catch (error) {
      console.error('Error eliminando tipo:', error);
      toastError('Error al eliminar el tipo');
    } finally {
      setLoading(false);
    }
  };

  const cancelTypeDelete = () => {
    setShowDeleteTypeModal(false);
    setDeletingType(null);
  };

  // Funciones de eliminación PERMANENTE de accesorios
  const handlePermanentDelete = (accessory) => {
    setPermanentDeletingAccessory(accessory);
    setShowPermanentDeleteModal(true);
  };

  const confirmPermanentDelete = async () => {
    if (!permanentDeletingAccessory) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        id: permanentDeletingAccessory.id,
        user: 1
      };
      
      const response = await deleteData('/api/admin/delete_accessory', dataToSend);
      
      if (response?.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al eliminar');
      }
      
      // Actualización optimista: eliminar de la lista
      setAccessories(prevAccessories => prevAccessories.filter(accessory => accessory.id !== permanentDeletingAccessory.id));
      
      toastSuccess(`Accesorio "${permanentDeletingAccessory.accessory_name}" eliminado permanentemente`);
      setShowPermanentDeleteModal(false);
      setPermanentDeletingAccessory(null);
    } catch (error) {
      console.error('Error eliminando accesorio:', error);
      toastError(error.message || 'Error al eliminar el accesorio definitivamente');
    } finally {
      setLoading(false);
    }
  };

  const cancelPermanentDelete = () => {
    setShowPermanentDeleteModal(false);
    setPermanentDeletingAccessory(null);
  };

  // Funciones de eliminación PERMANENTE de tipos
  const handlePermanentDeleteType = (type) => {
    setPermanentDeletingType(type);
    setShowPermanentDeleteTypeModal(true);
  };

  const confirmPermanentDeleteType = async () => {
    if (!permanentDeletingType) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        id: permanentDeletingType.id,
        user: 1
      };
      
      const response = await deleteData('/api/admin/delete_accessory_type', dataToSend);
      
      if (response?.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al eliminar');
      }
      
      // Actualización optimista: eliminar de la lista
      setAccessoryTypes(prevTypes => prevTypes.filter(type => type.id !== permanentDeletingType.id));
      
      toastSuccess(`Tipo "${permanentDeletingType.accessory_type_name}" eliminado permanentemente`);
      setShowPermanentDeleteTypeModal(false);
      setPermanentDeletingType(null);
    } catch (error) {
      console.error('Error eliminando tipo:', error);
      toastError(error.message || 'Error al eliminar el tipo definitivamente');
    } finally {
      setLoading(false);
    }
  };

  const cancelPermanentDeleteType = () => {
    setShowPermanentDeleteTypeModal(false);
    setPermanentDeletingType(null);
  };

  // Filtrar accesorios y tipos según los toggles - Optimizado con useMemo
  const filteredAccessories = useMemo(() => {
    return showInactiveAccessories ? accessories : accessories.filter(accessory => accessory.active);
  }, [accessories, showInactiveAccessories]);

  const filteredAccessoryTypes = useMemo(() => {
    return showInactiveTypes ? accessoryTypes : accessoryTypes.filter(type => type.active);
  }, [accessoryTypes, showInactiveTypes]);

  return (
    <div className="accessories-manager">
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-3">
            <FaPuzzlePiece className="me-2 text-primary" />
            Gestión de Accesorios
          </h5>
          
          {/* Pestañas de navegación */}
          <ul className="nav nav-tabs" id="accessoriesTab" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'accessories' ? 'active' : ''}`}
                onClick={() => setActiveTab('accessories')}
                type="button"
                disabled={loading}
              >
                <FaList className="me-2" />
                Accesorios
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'types' ? 'active' : ''}`}
                onClick={() => setActiveTab('types')}
                type="button"
                disabled={loading}
              >
                <FaTags className="me-2" />
                Tipos de Accesorios
              </button>
            </li>
          </ul>
        </div>
        
        <div className="card-body">
          {/* Contenido de la pestaña Accesorios */}
          {activeTab === 'accessories' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Lista de Accesorios</h6>
                <div className="d-flex gap-2 align-items-center">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showInactiveAccessoriesToggle"
                      checked={showInactiveAccessories}
                      onChange={(e) => setShowInactiveAccessories(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showInactiveAccessoriesToggle">
                      Mostrar inactivos
                    </label>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleNew}
                    disabled={loading}
                  >
                    <FaPlus className="me-2" />
                    Nuevo Accesorio
                  </button>
                </div>
              </div>
              
              {!showInactiveAccessories && accessories.length > filteredAccessories.length && (
                <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <i className="fas fa-info-circle me-2"></i>
                    Hay <strong>{accessories.length - filteredAccessories.length}</strong> elemento(s) inactivo(s) oculto(s).
                  </div>
                  <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowInactiveAccessories(true)}
                  >
                    Mostrar todos
                  </button>
                </div>
              )}
              
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <div className="mt-2 text-muted">Cargando accesorios...</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Nombre</th>
                        <th>Tipo</th>
                        <th>Estado</th>
                        <th>Fecha Creación</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAccessories.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center text-muted py-4">
                            {showInactiveAccessories ? 'No hay accesorios registrados' : 'No hay accesorios activos. Activa el toggle "Mostrar inactivos" para ver accesorios eliminados.'}
                          </td>
                        </tr>
                      ) : (
                        filteredAccessories.map(accessory => (
                          <tr key={accessory.id}>
                            <td>#{accessory.id}</td>
                            <td>
                              <strong>{accessory.accessory_name}</strong>
                            </td>
                            <td>
                              <span className="badge bg-info">
                                {accessory.accessory_type_name}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${accessory.active ? 'bg-success' : 'bg-danger'}`}>
                                {accessory.active ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(accessory.inserted_date).toLocaleDateString('es-AR')}
                              </small>
                            </td>
                            <td className="text-center">
                              <div className="btn-group" role="group">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleEdit(accessory)}
                                  disabled={loading}
                                  title="Editar accesorio"
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => handleDelete(accessory)}
                                  disabled={loading}
                                  title={accessory.active ? "Inactivar accesorio" : "Activar accesorio"}
                                >
                                  <FaTrash />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handlePermanentDelete(accessory)}
                                  disabled={loading}
                                  title="Eliminar definitivamente (irreversible)"
                                >
                                  <FaExclamationTriangle />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Contenido de la pestaña Tipos de Accesorios */}
          {activeTab === 'types' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Lista de Tipos de Accesorios</h6>
                <div className="d-flex gap-2 align-items-center">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showInactiveTypesToggle"
                      checked={showInactiveTypes}
                      onChange={(e) => setShowInactiveTypes(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showInactiveTypesToggle">
                      Mostrar inactivos
                    </label>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={handleTypeNew}
                    disabled={loading}
                  >
                    <FaPlus className="me-2" />
                    Nuevo Tipo
                  </button>
                </div>
              </div>
              
              {!showInactiveTypes && accessoryTypes.length > filteredAccessoryTypes.length && (
                <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <i className="fas fa-info-circle me-2"></i>
                    Hay <strong>{accessoryTypes.length - filteredAccessoryTypes.length}</strong> elemento(s) inactivo(s) oculto(s).
                  </div>
                  <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowInactiveTypes(true)}
                  >
                    Mostrar todos
                  </button>
                </div>
              )}
              
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                  <div className="mt-2 text-muted">Cargando tipos...</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Nombre del Tipo</th>
                        <th>Estado</th>
                        <th>Fecha Creación</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAccessoryTypes.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center text-muted py-4">
                            {showInactiveTypes ? 'No hay tipos de accesorios registrados' : 'No hay tipos activos. Activa el toggle "Mostrar inactivos" para ver tipos eliminados.'}
                          </td>
                        </tr>
                      ) : (
                        filteredAccessoryTypes.map(type => (
                          <tr key={type.id}>
                            <td>#{type.id}</td>
                            <td>
                              <strong>{type.accessory_type_name}</strong>
                            </td>
                            <td>
                              <span className={`badge ${type.active ? 'bg-success' : 'bg-danger'}`}>
                                {type.active ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(type.inserted_date).toLocaleDateString('es-AR')}
                              </small>
                            </td>
                            <td className="text-center">
                              <div className="btn-group" role="group">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleTypeEdit(type)}
                                  disabled={loading}
                                  title="Editar tipo"
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => handleTypeDelete(type)}
                                  disabled={loading}
                                  title={type.active ? "Inactivar tipo" : "Activar tipo"}
                                >
                                  <FaTrash />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handlePermanentDeleteType(type)}
                                  disabled={loading}
                                  title="Eliminar definitivamente (irreversible)"
                                >
                                  <FaExclamationTriangle />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar accesorio */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingAccessory ? 'Editar Accesorio' : 'Nuevo Accesorio'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Tipo de Accesorio *</label>
                    <select
                      className="form-select"
                      name="accessory_type"
                      value={formData.accessory_type}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    >
                      <option value="">Seleccionar tipo</option>
                      {accessoryTypes
                        .filter(type => type.active)
                        .map(type => (
                          <option key={type.id} value={type.id}>
                            {type.accessory_type_name}
                          </option>
                        ))}
                    </select>
                    <div className="form-text">
                      Selecciona el tipo de accesorio correspondiente
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Nombre del Accesorio *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="accessory_name"
                      value={formData.accessory_name}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      placeholder="Ej: PATA DE MADERA 15CM, MANIJA CROMADA, etc."
                    />
                    <div className="form-text">
                      Ingresa un nombre descriptivo para el accesorio
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="active"
                        checked={formData.active === 1}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          active: e.target.checked ? 1 : 0
                        }))}
                        disabled={loading}
                      />
                      <label className="form-check-label">
                        Accesorio activo
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.accessory_name.trim() || !formData.accessory_type}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingAccessory ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar el accesorio <strong>"{deletingAccessory?.accessory_name}"</strong>?</p>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  El accesorio será marcado como inactivo y no aparecerá en las opciones disponibles.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para crear/editar tipo de accesorio */}
      {showTypeModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingType ? 'Editar Tipo de Accesorio' : 'Nuevo Tipo de Accesorio'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowTypeModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleTypeSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Nombre del Tipo *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="accessory_type_name"
                      value={typeFormData.accessory_type_name}
                      onChange={handleTypeInputChange}
                      required
                      disabled={loading}
                      placeholder="Ej: PATAS, COJINES, RUEDAS, etc."
                    />
                    <div className="form-text">
                      Ingresa el nombre del tipo de accesorio en mayúsculas
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="active"
                        checked={typeFormData.active === 1}
                        onChange={(e) => setTypeFormData(prev => ({
                          ...prev,
                          active: e.target.checked ? 1 : 0
                        }))}
                        disabled={loading}
                      />
                      <label className="form-check-label">
                        Tipo activo
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowTypeModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !typeFormData.accessory_type_name.trim()}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingType ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación de tipo */}
      {showDeleteTypeModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelTypeDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar el tipo <strong>"{deletingType?.accessory_type_name}"</strong>?</p>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  El tipo será marcado como inactivo y no aparecerá en las opciones disponibles para nuevos accesorios.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelTypeDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmTypeDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación PERMANENTE de accesorio */}
      {showPermanentDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="me-2" />
                  ⚠️ ELIMINAR DEFINITIVAMENTE
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={cancelPermanentDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <strong>⚠️ ADVERTENCIA: Esta acción es IRREVERSIBLE</strong>
                </div>
                <p>¿Está COMPLETAMENTE seguro que desea eliminar permanentemente el siguiente accesorio?</p>
                {permanentDeletingAccessory && (
                  <div className="alert alert-warning">
                    <strong>Accesorio:</strong> {permanentDeletingAccessory.accessory_name}<br/>
                    <strong>Tipo:</strong> {permanentDeletingAccessory.accessory_type_name}
                  </div>
                )}
                <ul className="text-danger">
                  <li>El accesorio se eliminará <strong>permanentemente</strong> de la base de datos</li>
                  <li>NO se podrá recuperar esta información</li>
                  <li>Puede afectar pedidos y referencias existentes</li>
                </ul>
                <p className="mt-3">
                  <small className="text-muted">
                    💡 <strong>Recomendación:</strong> Si solo desea ocultar temporalmente este accesorio,
                    use el botón de "Inactivar" en su lugar.
                  </small>
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelPermanentDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmPermanentDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaExclamationTriangle className="me-2" />
                      ⚠️ SÍ, ELIMINAR DEFINITIVAMENTE
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación PERMANENTE de tipo */}
      {showPermanentDeleteTypeModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="me-2" />
                  ⚠️ ELIMINAR DEFINITIVAMENTE
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={cancelPermanentDeleteType}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <strong>⚠️ ADVERTENCIA: Esta acción es IRREVERSIBLE</strong>
                </div>
                <p>¿Está COMPLETAMENTE seguro que desea eliminar permanentemente el siguiente tipo de accesorio?</p>
                {permanentDeletingType && (
                  <div className="alert alert-warning">
                    <strong>Tipo:</strong> {permanentDeletingType.accessory_type_name}
                  </div>
                )}
                <ul className="text-danger">
                  <li>El tipo de accesorio se eliminará <strong>permanentemente</strong> de la base de datos</li>
                  <li>NO se podrá recuperar esta información</li>
                  <li>Puede afectar accesorios y referencias existentes</li>
                </ul>
                <p className="mt-3">
                  <small className="text-muted">
                    💡 <strong>Recomendación:</strong> Si solo desea ocultar temporalmente este tipo,
                    use el botón de "Inactivar" en su lugar.
                  </small>
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelPermanentDeleteType}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmPermanentDeleteType}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaExclamationTriangle className="me-2" />
                      ⚠️ SÍ, ELIMINAR DEFINITIVAMENTE
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccessoriesManager;